import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  unsubscribeCurrentIdentityFromGroups,
  getCurrentIdentityGroupsRecursively,
  getCurrentIdentityGroups,
} from "../../actions/api";
import { isApiCallLoading } from "../../selectors";
import { errorAction, types } from "@authzsvc/api-lib";
import { ContentTable, SearchBar } from "@authzsvc/common-react-components";
import {
  defaultGroupTableColumns,
  DEFAULT_GROUP_TABLE_QUERY_FIELDS,
} from "../../constants";
import {
  Button,
  Space,
  Spin,
  Card,
  Typography,
  Modal,
  List,
  Tooltip,
} from "antd";
import { TeamOutlined, DeleteOutlined } from "@ant-design/icons";
import { showErrorNotification, showSuccessNotification } from "../../util";

// Query params for fetching groups with selfSubscriptionType
const BASE_QUERY_PARAMS = {
  field: [...DEFAULT_GROUP_TABLE_QUERY_FIELDS, "selfSubscriptionType"],
};

const MyGroupMemberships = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loadingUnsubscribe, setLoadingUnsubscribe] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isUnsubscribeModalOpen, setIsUnsubscribeModalOpen] = useState(false);

  const dispatch = useDispatch();
  const loading = useSelector((state) =>
    isApiCallLoading(state, types.GET_CURRENT_IDENTITY_GROUPS_RECURSIVELY)
  );
  const directMembershipGroups = useSelector(
    (state) => state.groupMembership.direct
  );
  const recursiveMembershipGroups = useSelector(
    (state) => state.groupMembership.recursive
  );
  const currentIdentity = useSelector((state) => state.identity);

  // Merge direct and recursive groups, removing duplicates
  // This ensures immediate updates after unsubscribe since direct endpoint updates faster
  const groups = useMemo(() => {
    const groupMap = new Map();

    // Add direct groups
    directMembershipGroups.forEach((group) => {
      groupMap.set(group.id, group);
    });

    // Add recursive groups (will overwrite if duplicate)
    recursiveMembershipGroups.forEach((group) => {
      groupMap.set(group.id, group);
    });

    return Array.from(groupMap.values());
  }, [directMembershipGroups, recursiveMembershipGroups]);

  // Fetch groups with optional search filter
  const fetchGroups = useCallback(
    (searchFilter = "", filterType = "Contains") => {
      const queryParams = { ...BASE_QUERY_PARAMS };
      if (searchFilter.trim()) {
        queryParams.filter = `groupIdentifier:${filterType}:${searchFilter.trim()}`;
      }
      dispatch(getCurrentIdentityGroups(queryParams));
      dispatch(getCurrentIdentityGroupsRecursively(queryParams));
    },
    [dispatch]
  );

  // Initial load - fetch all groups
  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Compute selected groups on-demand from selectedRowKeys
  const selectedGroups = useMemo(
    () => groups.filter((group) => selectedRowKeys.includes(group.id)),
    [groups, selectedRowKeys]
  );

  const onReset = useCallback(() => {
    setSelectedRowKeys([]);
  }, []);

  // Check if current user is a CERN user
  const isCernUser = currentIdentity?.source?.toLowerCase() === "cern";

  // Check if user has direct membership in a group
  const hasDirectMembership = (groupId) =>
    directMembershipGroups.some((group) => group.id === groupId);

  // Only allow unsubscription from:
  // 1. GMS groups (non-replica)
  // 2. Static groups (non-dynamic)
  // 3. Groups with direct membership
  // 4. Groups that allow self-unsubscription:
  //    - selfSubscriptionType is "Open"
  //    - selfSubscriptionType is "CernUsers" AND user is a CERN user
  const isSelectableGroup = (group) => {
    // Must be GMS (non-replica) and static (non-dynamic)
    if (group.syncType === "Slave" || group.dynamic === true) {
      return false;
    }

    // Must have direct membership
    if (!hasDirectMembership(group.id)) {
      return false;
    }

    // Check self-subscription type
    if (group.selfSubscriptionType === "Closed") {
      return false;
    }
    if (group.selfSubscriptionType === "CernUsers" && !isCernUser) {
      return false;
    }

    return true;
  };

  const getDisabledReason = (group) => {
    if (group.syncType === "Slave")
      return "You cannot unsubscribe from replica groups in the Groups Portal.";
    if (group.dynamic === true)
      return "You cannot unsubscribe from dynamic groups.";
    if (!hasDirectMembership(group.id))
      return "You can only unsubscribe from groups where you have direct membership. You are a member of this group through another group.";
    if (group.selfSubscriptionType === "Closed")
      return "This group does not allow self-unsubscription. If you are an owner, admin, or moderator, you can remove yourself from the 'Groups I own or manage' tab. Otherwise, contact the group owner to be removed.";
    if (group.selfSubscriptionType === "CernUsers" && !isCernUser)
      return "This group only allows CERN users to self-unsubscribe. Contact the group owner to be removed.";
    return "";
  };

  const onUnsubscribe = useCallback(async () => {
    setLoadingUnsubscribe(true);

    const response = await dispatch(
      unsubscribeCurrentIdentityFromGroups(selectedRowKeys)
    );

    if (
      response.type ===
      errorAction(types.UNSUBSCRIBE_CURRENT_IDENTITY_FROM_GROUPS)
    ) {
      showErrorNotification(
        response.payload.title,
        response.payload.data.message
      );
      setLoadingUnsubscribe(false);
    } else {
      showSuccessNotification(
        "Unsubscribed successfully",
        `You have been unsubscribed from ${selectedRowKeys.length} group${
          selectedRowKeys.length === 1 ? "" : "s"
        }.`
      );
      setIsUnsubscribeModalOpen(false);
      onReset();
      // Refresh group memberships with current search
      fetchGroups(searchValue);
      setLoadingUnsubscribe(false);
    }
  }, [dispatch, selectedRowKeys, onReset, fetchGroups, searchValue]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys, rows) => {
      // Only allow valid group keys
      const filteredKeys = keys.filter((key) => {
        const group = groups.find((g) => g.id === key);
        return group && isSelectableGroup(group);
      });
      setSelectedRowKeys(filteredKeys);
    },
    getCheckboxProps: (record) => {
      const disabled = !isSelectableGroup(record);
      return {
        disabled,
      };
    },
    renderCell: (checked, record, index, originNode) => {
      const disabled = !isSelectableGroup(record);
      const reason = getDisabledReason(record);
      if (disabled && reason) {
        return (
          <Tooltip title={reason} placement="right">
            <span>{originNode}</span>
          </Tooltip>
        );
      }
      return originNode;
    },
  };

  const hasSelected = selectedRowKeys.length > 0;

  // Reset to first page when groups change
  useEffect(() => {
    setCurrentPage(1);
  }, [groups]);

  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          <TeamOutlined style={{ marginRight: 8, color: "#bfbfbf" }} />
          Groups I'm in
        </Typography.Title>
      }
      extra={
        <Space>
          {hasSelected && (
            <>
              <Typography.Text type="secondary">
                Selected {selectedRowKeys.length} items
              </Typography.Text>
              <Button
                id="resetSelectionButton"
                type="primary"
                ghost
                onClick={onReset}
                disabled={!hasSelected || loadingUnsubscribe}
                loading={loadingUnsubscribe}
              >
                Reset selection
              </Button>
            </>
          )}
          <Tooltip
            title={
              "You can unsubscribe from static GMS groups that allow self-subscription and in which you are a direct member. Changes may take 15 minutes to be effective."
            }
            placement="right"
          >
            <Button
              id="bulkUnsubscribeButton2"
              type="primary"
              danger
              onClick={() => setIsUnsubscribeModalOpen(true)}
              disabled={!hasSelected || loadingUnsubscribe}
              loading={loadingUnsubscribe}
            >
              Bulk unsubscribe
            </Button>
          </Tooltip>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <div style={{ marginBottom: 24 }}>
          <SearchBar
            onSearch={fetchGroups}
            searchValue={searchValue}
            onSearchValueChange={setSearchValue}
            filter={true}
            filterPlacement="before"
            allowClear={true}
            placeholder="Search groups..."
          />
        </div>
        <ContentTable
          rowKey="id"
          pagination={{
            current: currentPage,
            onChange: (page) => setCurrentPage(page),
            onShowSizeChange: (current, size) => setPageSize(size),
            showSizeChanger: true,
            hideOnSinglePage: true,
            showTotal: (total) =>
              `Total of ${total} ${total === 1 ? "group" : "groups"}`,
            defaultPageSize: 10,
          }}
          rowSelection={rowSelection}
          columns={defaultGroupTableColumns}
          dataSource={groups}
        />
        {groups.length <= pageSize && groups.length > 0 && (
          <div style={{ marginTop: 16, textAlign: "right" }}>
            <div
              style={{
                display: "inline-block",
                height: "32px",
                lineHeight: "30px",
                color: "rgba(0, 0, 0, 0.88)",
                fontSize: "14px",
              }}
            >
              Total of {groups.length}{" "}
              {groups.length === 1 ? "group" : "groups"}
            </div>
          </div>
        )}
      </Spin>

      {/* Bulk Unsubscribe Confirmation Modal */}
      <Modal
        title={
          <span>
            <DeleteOutlined style={{ marginRight: 8, color: "#ff4d4f" }} />
            Confirm Bulk Unsubscription
          </span>
        }
        centered
        open={isUnsubscribeModalOpen}
        onCancel={() => setIsUnsubscribeModalOpen(false)}
        onOk={onUnsubscribe}
        okButtonProps={{ danger: true, loading: loadingUnsubscribe }}
        cancelButtonProps={{ disabled: loadingUnsubscribe }}
        closable={!loadingUnsubscribe}
        maskClosable={!loadingUnsubscribe}
      >
        <p>Are you sure you want to unsubscribe from the following groups?</p>
        <List
          dataSource={selectedGroups}
          renderItem={(group) => (
            <List.Item>
              <Typography.Text strong>{group.groupIdentifier}</Typography.Text>
              {group.description && (
                <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                  {group.description}
                </Typography.Text>
              )}
            </List.Item>
          )}
          bordered
          size="small"
        />
      </Modal>
    </Card>
  );
};

export default MyGroupMemberships;
